// CHANGE NUDGE STORE (card #3562, docs/adr/0003-one-change-signal-change-checks-and-staff-nudges.md).
//
// The one place a staff screen's CHANGE CHECK learns what the staff WebSocket is
// doing, without every call site threading a socket through props. The socket
// (nudgeSocket.js, mounted once per app by useChangeNudgeSocket) writes here;
// each change check (announcer-fe, scoreadmin-fe, score-fe, scribe-fe) reads:
//
//   - subscribeNudgeStore(showId, fn): fn({ kind: "nudge" }) -> run the change
//     check NOW; fn({ kind: "health", healthy }) -> the socket just went
//     healthy / down, so re-time the beat and, on down, check at once.
//   - changeCheckBeatMs(showId, pollMs): the beat to use. While the socket is
//     healthy the change check only backstops the nudge (every 30 s, never
//     faster than the server's own pollMs). The moment the socket is down, or
//     was never opened (Instant updates OFF, no WebSocket URL, public screen),
//     it is the normal rate (the server's pollMs, default 5 s).
//   - notifyShowMoved(showId): a change check saw the show move. If the socket
//     was told "stay on change checks" (Instant updates OFF), this is the cue to
//     ask again whether it is ON now: the server sends no signal when the
//     setting is switched back on (card #3561).
//
// Module state, keyed by show id. A screen that never mounts the socket (the
// public phone board, a show with Instant updates OFF) simply never has a
// healthy entry, so every read answers "not healthy" and the check runs at the
// normal rate: the store is inert without a socket.

// The change-check beat while the socket is healthy.
export const HEALTHY_CHECK_MS = 30 * 1000;

const entries = new Map();

function entry(showId) {
  let e = entries.get(showId);
  if (!e) {
    e = { healthy: false, listeners: new Set(), probers: new Set() };
    entries.set(showId, e);
  }
  return e;
}

function emit(showId, event) {
  const e = entries.get(showId);
  if (!e) return;
  for (const fn of [...e.listeners]) {
    try {
      fn(event);
    } catch {
      // one bad listener must not stop the others
    }
  }
}

export function isSocketHealthy(showId) {
  return showId != null && entries.get(showId)?.healthy === true;
}

// The beat a change check should run at right now.
export function changeCheckBeatMs(showId, pollMs) {
  return isSocketHealthy(showId) ? Math.max(HEALTHY_CHECK_MS, pollMs) : pollMs;
}

export function setSocketHealthy(showId, healthy) {
  if (showId == null) return;
  const e = entry(showId);
  const next = healthy === true;
  if (e.healthy === next) return;
  e.healthy = next;
  emit(showId, { kind: "health", healthy: next });
}

export function emitNudge(showId) {
  if (showId == null) return;
  emit(showId, { kind: "nudge" });
}

export function subscribeNudgeStore(showId, fn) {
  if (showId == null) return () => {};
  const e = entry(showId);
  e.listeners.add(fn);
  return () => {
    e.listeners.delete(fn);
  };
}

// The socket registers how to re-ask "is Instant updates on now?".
export function registerProber(showId, fn) {
  if (showId == null) return () => {};
  const e = entry(showId);
  e.probers.add(fn);
  return () => {
    e.probers.delete(fn);
  };
}

export function notifyShowMoved(showId) {
  if (showId == null) return;
  const e = entries.get(showId);
  if (!e) return;
  for (const fn of [...e.probers]) {
    try {
      fn();
    } catch {
      // a failed probe leaves the screen on change checks
    }
  }
}

// Test seam.
export function resetNudgeStore() {
  entries.clear();
}
